import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { smartHubApi, organizationApi } from '../../services/api';
import type {
  BillingGapsFeed,
  SmartHubAnswerRow,
  SmartHubEhrChangeRow,
  SmartHubReadingRow,
  SmartHubTaskRow,
  TaskBarFeed,
} from './types';

const TASK_BAR_POLL_MS = 60_000;
// Matches the 5-min server-side LRU cache on /session/smart_hub/billing_gaps
// — extra requests are essentially free but burst less.
const BILLING_POLL_MS = 5 * 60_000;
// PHA-714: minimum gap between focus/visibility-triggered refetches so we
// don't hammer the backend on rapid tab-switches.
const SMARTHUB_VIS_REFETCH_THROTTLE_MS = 10_000;

interface SmartHubFeedState {
  taskBar: TaskBarFeed | null;
  billing: BillingGapsFeed | null;
  // True when billing_gaps returned 403 — user lacks billing role; hide the strip.
  billingDenied: boolean;
  // PHA-906: programs the selected clinic supports, so the billing strip can hide
  // a whole sub-section (not just show it at 0) for a program the clinic doesn't
  // bill. 'ALL'/unset -> all three (hide nothing), matching PatientList's column
  // gating. The backend already empties the unsupported category's rows; this only
  // drives whether the sub-section header renders at all.
  enabledPrograms: Set<string>;
  loading: boolean;
  lastUpdated: Date | null;
  error: string | null;
  refetchTaskBar: () => Promise<void>;
}

const ALL_PROGRAMS = ['APCM', 'CCM', 'RPM'];

const empty: TaskBarFeed = {
  todo: [],
  out_of_bounds_vital_signs: [],
  hospitalizations: [],
  ehr_changes_pending: [],
  overdue_upcoming_tasks: [],
  new_vital_signs: [],
  new_patient_answers: [],
  new_device_readings: [],
};

// PHA-509: when `patientId` is provided we filter every bucket of the clinic
// feed down to that patient. This is the minimum-viable patient-context
// Smart Hub — no backend change required, since each row already carries a
// `patient_id`.
const filterFeedByPatient = (
  feed: TaskBarFeed | null,
  patientId: string | undefined,
): TaskBarFeed | null => {
  if (!feed) return feed;
  if (!patientId) return feed;
  const sameP = (r: { patient_id: string }) => r.patient_id === patientId;
  return {
    todo: feed.todo.filter(sameP),
    out_of_bounds_vital_signs: feed.out_of_bounds_vital_signs.filter(sameP) as (
      | SmartHubAnswerRow
      | SmartHubReadingRow
    )[],
    hospitalizations: feed.hospitalizations.filter(sameP) as SmartHubAnswerRow[],
    // PHA-510: filter EHR-change rows by patient_id in patient-scoped mode.
    ehr_changes_pending: feed.ehr_changes_pending.filter(
      sameP,
    ) as SmartHubEhrChangeRow[],
    overdue_upcoming_tasks: feed.overdue_upcoming_tasks.filter(
      sameP,
    ) as SmartHubTaskRow[],
    new_vital_signs: feed.new_vital_signs.filter(sameP) as (
      | SmartHubAnswerRow
      | SmartHubReadingRow
    )[],
    new_patient_answers: feed.new_patient_answers.filter(
      sameP,
    ) as SmartHubAnswerRow[],
    new_device_readings: feed.new_device_readings.filter(
      sameP,
    ) as SmartHubReadingRow[],
  };
};

// PHA-509 follow-up: per the AC ("their alerts, their tasks, their billing
// gaps") the Billing opportunities strip should appear in patient mode too,
// scoped to that patient. Each billing row carries patient_id, so the same
// client-side filter pattern applies.
const filterBillingByPatient = (
  feed: BillingGapsFeed | null,
  patientId: string | undefined,
): BillingGapsFeed | null => {
  if (!feed) return feed;
  if (!patientId) return feed;
  const sameP = (r: { patient_id: string }) => r.patient_id === patientId;
  return {
    ccm_gap_to_20: feed.ccm_gap_to_20.filter(sameP),
    rpm_minutes_to_10: (feed.rpm_minutes_to_10 ?? []).filter(sameP),
    apcm_no_activity_this_month: feed.apcm_no_activity_this_month.filter(sameP),
    rpm_days_short: feed.rpm_days_short.filter(sameP),
  };
};

export function useSmartHubFeed(
  organizationId?: string,
  patientId?: string,
): SmartHubFeedState {
  const [taskBar, setTaskBar] = useState<TaskBarFeed | null>(null);
  const [billing, setBilling] = useState<BillingGapsFeed | null>(null);
  const [billingDenied, setBillingDenied] = useState(false);
  // PHA-906: default to all-three so nothing is hidden until resolved / on error
  // (the billing gate remains the source of truth for the rows themselves).
  const [enabledPrograms, setEnabledPrograms] = useState<Set<string>>(
    new Set(ALL_PROGRAMS),
  );
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // PHA-714: tracks the timestamp of the last visibility/focus-triggered refetch.
  const lastVisibleRefetchRef = useRef<number>(0);

  // PHA-906: resolve the selected clinic's supported programs once per org switch
  // (config rarely changes mid-session, so this is NOT on either poll interval).
  // Mirrors PatientList.tsx: 'ALL'/unset -> all three; errors degrade to all three.
  useEffect(() => {
    let cancelled = false;
    if (!organizationId || organizationId === 'ALL') {
      setEnabledPrograms(new Set(ALL_PROGRAMS));
      return;
    }
    organizationApi
      .getEffectiveSupportedPrograms(organizationId)
      .then((data: any) => {
        if (cancelled) return;
        const progs = Array.isArray(data?.programs) ? data.programs : ALL_PROGRAMS;
        setEnabledPrograms(new Set(progs));
      })
      .catch(() => {
        if (!cancelled) setEnabledPrograms(new Set(ALL_PROGRAMS));
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const fetchTaskBar = useCallback(async (signal: AbortSignal) => {
    try {
      const data = await smartHubApi.taskBar(signal, organizationId);
      if (signal.aborted) return;
      // Backend may return either the feed shape OR an error envelope; accept both.
      if (data && typeof data === 'object' && 'todo' in data) {
        setTaskBar(data as TaskBarFeed);
      } else {
        setTaskBar(empty);
      }
      setLastUpdated(new Date());
      setError(null);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      // eslint-disable-next-line no-console
      console.warn('Smart Hub task_bar fetch failed', err);
      setError('Smart Hub feed unavailable');
    }
  }, [organizationId]);

  const fetchBilling = useCallback(async (signal: AbortSignal) => {
    try {
      const { status, data } = await smartHubApi.billingGaps(signal, organizationId);
      if (signal.aborted) return;
      if (status === 403) {
        setBillingDenied(true);
        setBilling(null);
        return;
      }
      if (status >= 200 && status < 300 && data && typeof data === 'object') {
        setBilling(data as BillingGapsFeed);
        setBillingDenied(false);
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      // eslint-disable-next-line no-console
      console.warn('Smart Hub billing_gaps fetch failed', err);
    }
  }, [organizationId]);

  // Refetch task bar on demand (e.g., after an ack)
  const refetchTaskBar = useCallback(async () => {
    const c = new AbortController();
    await fetchTaskBar(c.signal);
  }, [fetchTaskBar]);

  // PHA-1141 / PHA-1142: an acknowledgment made in the patient chart (not through the Smart
  // Hub) announces itself so the row it cleared goes now, not at the next poll.
  useEffect(() => {
    const onRefresh = () => {
      void refetchTaskBar();
    };
    window.addEventListener('phanova:smart-hub-refresh', onRefresh);
    return () => window.removeEventListener('phanova:smart-hub-refresh', onRefresh);
  }, [refetchTaskBar]);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;

    const initial = async () => {
      await Promise.all([fetchTaskBar(controller.signal), fetchBilling(controller.signal)]);
      if (!cancelled) setLoading(false);
    };
    initial();

    const taskBarTimer = setInterval(() => {
      if (!cancelled) fetchTaskBar(controller.signal);
    }, TASK_BAR_POLL_MS);
    const billingTimer = setInterval(() => {
      if (!cancelled) fetchBilling(controller.signal);
    }, BILLING_POLL_MS);

    // PHA-714: recover immediately when the tab regains focus instead of waiting
    // up to a full poll interval; a successful fetch clears the "feed unavailable" error.
    const onVisible = () => {
      if (cancelled || document.hidden) return;
      const now = Date.now();
      if (now - lastVisibleRefetchRef.current < SMARTHUB_VIS_REFETCH_THROTTLE_MS) return;
      lastVisibleRefetchRef.current = now;
      fetchTaskBar(controller.signal);
      fetchBilling(controller.signal);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(taskBarTimer);
      clearInterval(billingTimer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [fetchTaskBar, fetchBilling]);

  // Apply patient filter at the consumer layer. We keep the unfiltered feed
  // in state so switching patients (or back to clinic-wide) is instant — no
  // refetch needed.
  const filteredTaskBar = useMemo(
    () => filterFeedByPatient(taskBar, patientId),
    [taskBar, patientId],
  );
  const filteredBilling = useMemo(
    () => filterBillingByPatient(billing, patientId),
    [billing, patientId],
  );

  return {
    taskBar: filteredTaskBar,
    billing: filteredBilling,
    // Honor the clinic-wide 403 (user lacks billing role) regardless of mode
    // — patient context shouldn't leak billing rows the user couldn't see at
    // the clinic level.
    billingDenied,
    enabledPrograms,
    loading,
    lastUpdated,
    error,
    refetchTaskBar,
  };
}
